import { useCallback, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { CloseTemplate } from "@common/types/closeTemplate";
import {
    CloseTemplateBasis,
    CloseTemplateTypeFilter,
} from "@common/schemas/close-template.schema";
import {
    CloseTemplatesSortValue,
    CloseTemplatesView,
} from "@common/types/views/closeTemplatesView";
import { View } from "@common/types/views/views";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Button } from "@design-system/Button/Button";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { HStack } from "@design-system/HStack/HStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useGoToTemplateSchedules } from "@hooks/useGoToTemplateSchedules";
import {
    useCloseTemplates,
    useDuplicateCloseTemplateMutation,
} from "@api/closeTemplate.api";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { EditCloseTemplateDetailsDrawer } from "./EditCloseTemplateDetailsDrawer";
import { CloseTemplateTasksDrawer } from "./CloseTemplateTasksDrawer";
import { ApplyCloseTemplateDrawer } from "./ApplyCloseTemplateDrawer";
import { CloseTemplateApplicationsDrawer } from "./CloseTemplateApplicationsDrawer";
import {
    CloseTemplateRow,
    organizationCloseTemplatesViewConfig,
} from "./organizationCloseTemplatesViewConfig";
import { useCloseTemplateLifecycle } from "./useCloseTemplateLifecycle";
import { useCloseTemplateBulkActions } from "./useCloseTemplateBulkActions";
import {
    ArchiveFilter,
    matchesArchiveFilter,
} from "../templates/archiveFilter";
import { OrganizationCloseTemplatesEmptyState } from "./OrganizationCloseTemplatesEmptyState";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    search: string;
    serviceIds?: string[];
    ownerIds?: number[];
    noTier?: boolean;
    archiveFilter?: ArchiveFilter;
    typeFilter?: CloseTemplateTypeFilter;
    selectedBases?: CloseTemplateBasis[];
    selectedUpdatedByUserIds?: number[];
    sourceTaskTemplateIds?: string[];
    /** Restricts the list to a single template, e.g. when arriving via the "Go to template" schedule action. */
    templateId?: string | null;
    view: CloseTemplatesView;
    onViewChange: (view: View) => void;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    onCreateTemplate: () => void;
}

export interface CloseTemplateCallbacks {
    onManageTasks: (closeTemplateId: string) => void;
    onApplyCloseTemplate: (closeTemplateId: string) => void;
    onEditCloseTemplate: (closeTemplateId: string) => void;
    onDuplicateCloseTemplate: (closeTemplateId: string) => void;
    onDeleteCloseTemplate: (closeTemplateId: string) => void;
    onArchiveCloseTemplate: (closeTemplateId: string) => void;
    onUnarchiveCloseTemplate: (closeTemplateId: string) => void;
    onViewApplications: (closeTemplateId: string) => void;
    /** Navigates to the Schedules tab scoped to this template, instead of opening the old (now-removed) schedules drawer. */
    onViewSchedules: (closeTemplateId: string) => void;
}

export const OrganizationCloseTemplatesList = ({
    search,
    serviceIds,
    ownerIds,
    noTier,
    archiveFilter,
    typeFilter,
    selectedBases,
    selectedUpdatedByUserIds,
    sourceTaskTemplateIds,
    templateId,
    view,
    onViewChange,
    hasActiveFilters,
    onClearFilters,
    onCreateTemplate,
}: Props) => {
    const { toast: successToast, error: errorToast } = useToaster();
    const includeArchived =
        archiveFilter !== "active" && archiveFilter !== undefined;

    // Matches the backend default order (UPDATED_AT_DESC). Clearing an active
    // sort returns to this rather than an unsorted / insertion order.
    const [sort, setSort] = useState<CloseTemplatesSortValue>(
        CloseTemplatesSortValue.UPDATED_AT_DESC,
    );
    const handleSortChange = useCallback(
        (newSort: CloseTemplatesSortValue) => setSort(newSort),
        [],
    );
    const handleSortRemove = useCallback(
        () => setSort(CloseTemplatesSortValue.UPDATED_AT_DESC),
        [],
    );

    const {
        data: closeTemplates,
        isFetched,
        isError,
    } = useCloseTemplates({
        serviceIds,
        ownerIds,
        noTier,
        includeArchived,
        typeFilter,
        sourceTaskTemplateIds,
        sort,
    });

    const [managingTasksForTemplateId, setManagingTasksForTemplateId] =
        useState<string | null>(null);
    const [editingTemplate, setEditingTemplate] =
        useState<CloseTemplate | null>(null);
    const [applyingTemplate, setApplyingTemplate] =
        useState<CloseTemplate | null>(null);
    const [
        viewingApplicationsForTemplateId,
        setViewingApplicationsForTemplateId,
    ] = useState<string | null>(null);
    const [duplicatedTemplateId, setDuplicatedTemplateId] = useState<
        string | null
    >(null);
    const [duplicatedTemplate, setDuplicatedTemplate] =
        useState<CloseTemplate | null>(null);

    const duplicateTemplateMutation = useDuplicateCloseTemplateMutation();

    const templatesList = useMemo(() => closeTemplates ?? [], [closeTemplates]);

    const templatesById = useMemo(
        () => new Map(templatesList.map((t) => [t.id, t])),
        [templatesList],
    );

    const {
        handleDeleteCloseTemplate,
        handleArchiveCloseTemplate,
        handleUnarchiveCloseTemplate,
        lifecycleModals,
    } = useCloseTemplateLifecycle({ templatesList });

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const clearSelection = useCallback(() => setRowSelection({}), []);

    const filteredRows = useMemo<CloseTemplateRow[]>(() => {
        const lowerSearch = search.toLowerCase();
        return templatesList
            .filter((t) =>
                matchesArchiveFilter({
                    isArchived: t.archivedAt != null,
                    archiveFilter,
                }),
            )
            .filter(
                (t) => !search || t.name.toLowerCase().includes(lowerSearch),
            )
            .filter((t) => {
                if (!selectedBases || selectedBases.length === 0) {
                    return true;
                }
                return t.basis != null && selectedBases.includes(t.basis);
            })
            .filter((t) => {
                if (
                    !selectedUpdatedByUserIds ||
                    selectedUpdatedByUserIds.length === 0
                ) {
                    return true;
                }
                return (
                    t.updatedByUserId != null &&
                    selectedUpdatedByUserIds.includes(t.updatedByUserId)
                );
            })
            .filter((t) => !templateId || t.id === templateId)

            .map((t) => ({
                id: t.id,
                name: t.name,
                organizationId: t.organizationId,
                workspaceId: t.workspaceId,
                serviceId: t.serviceId,
                ownerId: t.ownerId,
                createdByUserId: t.createdByUserId,
                updatedByUserId: t.updatedByUserId,
                basis: t.basis,
                industries: t.industries,
                entityLegalTypes: t.entityLegalTypes,
                updatedAt: t.updatedAt,
                applicationCount: t.applicationCount,
                lastAppliedAt: t.lastAppliedAt,
                taskCount: t.taskCount,
                scheduleCount: t.scheduleCount,
                totalEstimatedTime: t.totalEstimatedTime,
                estimatedTimeByRole: t.estimatedTimeByRole,
                archivedAt: t.archivedAt,
            }));
    }, [
        templatesList,
        search,
        selectedBases,
        selectedUpdatedByUserIds,
        archiveFilter,
        templateId,
    ]);

    const viewWithBulkActions = useMemo<CloseTemplatesView>(
        () => ({
            ...view,
            options: {
                ...view.options,
                selection: true,
                bulkActions: true,
            },
        }),
        [view],
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            onViewChange({
                ...newView,
                options: {
                    ...newView.options,
                    selection: false,
                    bulkActions: false,
                },
            });
        },
        [onViewChange],
    );

    const rowsById = useMemo(
        () => new Map(filteredRows.map((row) => [row.id, row])),
        [filteredRows],
    );

    const selectedRows = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => rowsById.get(id))
                .filter((row): row is CloseTemplateRow => !!row),
        [rowSelection, rowsById],
    );

    const {
        handleOpenBulkActions,
        showDeleteConfirm: showBulkDeleteConfirm,
        setShowDeleteConfirm: setShowBulkDeleteConfirm,
        handleBulkDeleteConfirm,
        deleteEligibleCount: bulkDeleteEligibleCount,
        isBulkActionPending,
    } = useCloseTemplateBulkActions({
        selectedRows,
        clearSelection,
    });

    const selectedCount = selectedRows.length;

    const handleManageTasks = useCallback((closeTemplateId: string) => {
        setManagingTasksForTemplateId(closeTemplateId);
    }, []);

    const handleViewApplications = useCallback((closeTemplateId: string) => {
        setViewingApplicationsForTemplateId(closeTemplateId);
    }, []);

    // Clicking a template's schedule count used to open a drawer with no
    // useful content or actions (a dead end) — it now links to the Schedules
    // tab, pre-filtered to this template's schedules.
    const handleViewSchedules = useGoToTemplateSchedules();

    const handleEditCloseTemplate = useCallback(
        (closeTemplateId: string) => {
            const template = templatesById.get(closeTemplateId);
            if (template) {
                setEditingTemplate(template);
            }
        },
        [templatesById],
    );

    const handleApplyCloseTemplate = useCallback(
        (closeTemplateId: string) => {
            const template = templatesById.get(closeTemplateId);
            if (template) {
                setApplyingTemplate(template);
            }
        },
        [templatesById],
    );

    const handleDuplicateCloseTemplate = useCallback(
        async (closeTemplateId: string) => {
            if (duplicateTemplateMutation.isPending) {
                return;
            }
            const templateName =
                templatesById.get(closeTemplateId)?.name ?? "this template";
            try {
                const newTemplate =
                    await duplicateTemplateMutation.mutateAsync(
                        closeTemplateId,
                    );
                successToast(`Duplicated template "${templateName}"`);
                setDuplicatedTemplate(newTemplate);
            } catch {
                errorToast(`Failed to duplicate template "${templateName}"`);
            }
        },
        [duplicateTemplateMutation, errorToast, successToast, templatesById],
    );

    const tableMeta = useMemo<ViewTableMeta<CloseTemplateCallbacks, never>>(
        () => ({
            callbacks: {
                onManageTasks: handleManageTasks,
                onApplyCloseTemplate: handleApplyCloseTemplate,
                onEditCloseTemplate: handleEditCloseTemplate,
                onDuplicateCloseTemplate: handleDuplicateCloseTemplate,
                onDeleteCloseTemplate: handleDeleteCloseTemplate,
                onArchiveCloseTemplate: handleArchiveCloseTemplate,
                onUnarchiveCloseTemplate: handleUnarchiveCloseTemplate,
                onViewApplications: handleViewApplications,
                onViewSchedules: handleViewSchedules,
            },
        }),
        [
            handleManageTasks,
            handleApplyCloseTemplate,
            handleEditCloseTemplate,
            handleDuplicateCloseTemplate,
            handleDeleteCloseTemplate,
            handleArchiveCloseTemplate,
            handleUnarchiveCloseTemplate,
            handleViewApplications,
            handleViewSchedules,
        ],
    );

    return (
        <>
            <ViewTable
                data={isFetched ? filteredRows : null}
                view={viewWithBulkActions}
                config={organizationCloseTemplatesViewConfig}
                onViewChange={handleViewChange}
                sort={sort}
                onSortChange={handleSortChange}
                onSortRemove={handleSortRemove}
                emptyView={() => (
                    <OrganizationCloseTemplatesEmptyState
                        isError={isError}
                        hasActiveFilters={hasActiveFilters}
                        templateId={templateId}
                        onClearFilters={onClearFilters}
                        onCreateTemplate={onCreateTemplate}
                    />
                )}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                fixedTopOffsetPx
                enableColumnResizing
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                onRowClick={(row) => {
                    if (row) {
                        handleManageTasks(row.id);
                    }
                }}
                footer={
                    <CommandIsland
                        sticky
                        className={commandIslandStyles.bulkActionsStickyFooter}
                    >
                        <HStack gap={8}>
                            {filteredRows.length > 0 ? (
                                <CommandIslandTotal>
                                    {pluralizeWithCount(
                                        "template",
                                        filteredRows.length,
                                    )}
                                </CommandIslandTotal>
                            ) : undefined}

                            {selectedCount > 0 && (
                                <BulkSelectionControl
                                    size="md"
                                    selectedCount={selectedCount}
                                    onClearSelection={clearSelection}
                                />
                            )}
                        </HStack>

                        {selectedCount > 0 && (
                            <Tooltip content="Open bulk actions">
                                <Button
                                    variant="primary"
                                    size="md"
                                    rightIcon={
                                        <span data-slot-box="">
                                            <Shortcut keys="mod+k" />
                                        </span>
                                    }
                                    onClick={handleOpenBulkActions}
                                    disabled={isBulkActionPending}
                                >
                                    Actions
                                </Button>
                            </Tooltip>
                        )}
                    </CommandIsland>
                }
            />

            <CloseTemplateTasksDrawer
                open={!!managingTasksForTemplateId}
                closeTemplateId={managingTasksForTemplateId}
                onClose={() => setManagingTasksForTemplateId(null)}
            />

            <CloseTemplateTasksDrawer
                open={!!duplicatedTemplateId}
                closeTemplateId={duplicatedTemplateId}
                onClose={() => setDuplicatedTemplateId(null)}
            />

            <EditCloseTemplateDetailsDrawer
                open={!!editingTemplate}
                closeTemplate={editingTemplate}
                onClose={() => setEditingTemplate(null)}
            />

            <EditCloseTemplateDetailsDrawer
                open={!!duplicatedTemplate}
                closeTemplate={duplicatedTemplate}
                onClose={() => {
                    setDuplicatedTemplateId(duplicatedTemplate?.id ?? null);
                    setDuplicatedTemplate(null);
                }}
            />

            <ApplyCloseTemplateDrawer
                key={applyingTemplate?.id ?? "none"}
                open={!!applyingTemplate}
                closeTemplate={applyingTemplate ?? undefined}
                onClose={() => setApplyingTemplate(null)}
            />

            <CloseTemplateApplicationsDrawer
                open={!!viewingApplicationsForTemplateId}
                closeTemplateId={viewingApplicationsForTemplateId}
                templateName={
                    viewingApplicationsForTemplateId
                        ? (templatesById.get(viewingApplicationsForTemplateId)
                              ?.name ?? null)
                        : null
                }
                onClose={() => setViewingApplicationsForTemplateId(null)}
            />

            {lifecycleModals}

            <ConfirmationModal
                show={showBulkDeleteConfirm}
                title={`Delete ${bulkDeleteEligibleCount} ${pluralize("template", bulkDeleteEligibleCount)}?`}
                question="This will permanently remove the selected template(s). This action cannot be undone."
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onConfirm={handleBulkDeleteConfirm}
                onReject={() => setShowBulkDeleteConfirm(false)}
                onHide={() => setShowBulkDeleteConfirm(false)}
            />
        </>
    );
};
